import type { Session, User } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { hasSupabaseConfig } from '../lib/config';
import { supabase } from '../lib/supabase';
import { track } from '../lib/analytics';
import { saveAssessmentResult } from '../lib/api';
import {
  isCompleteAssessmentDraft,
  type AssessmentDraft
} from './assessmentDraft';

function readAssessmentDraft(): AssessmentDraft | null {
  try {
    const freeProfileRaw = window.localStorage.getItem(
      'orivon_professional_profile_v1'
    );
    const freeProfile = freeProfileRaw
      ? (JSON.parse(freeProfileRaw) as AssessmentDraft)
      : null;
    const sessionDraft = window.sessionStorage.getItem('orivon_assessment_draft_v1');
    let parsedSession = sessionDraft
      ? (JSON.parse(sessionDraft) as AssessmentDraft)
      : null;
    const handoffDraft = window.localStorage.getItem('orivon_assessment_handoff_v1');
    let parsedHandoff = handoffDraft
      ? (JSON.parse(handoffDraft) as AssessmentDraft & { handoffExpiresAt?: string })
      : null;
    if (
      parsedHandoff?.handoffExpiresAt &&
      Date.parse(parsedHandoff.handoffExpiresAt) <= Date.now()
    ) {
      window.localStorage.removeItem('orivon_assessment_handoff_v1');
      parsedHandoff = null;
    }
    if (
      parsedSession?.assessmentResultId &&
      parsedSession.assessmentResultId === freeProfile?.assessmentResultId
    ) {
      parsedSession = { ...parsedSession, saveToAccount: true };
    }
    if (parsedSession || parsedHandoff) {
      const sessionTime = Date.parse(parsedSession?.createdAt || '') || 0;
      const handoffTime = Date.parse(parsedHandoff?.createdAt || '') || 0;
      return handoffTime >= sessionTime ? parsedHandoff : parsedSession || parsedHandoff;
    }
    return freeProfile;
  } catch {
    return null;
  }
}

function assessmentSyncKey(userId: string, draft: AssessmentDraft | null) {
  return [
    userId,
    draft?.assessmentResultId || 'no-assessment',
    draft?.createdAt || 'no-created-at'
  ].join(':');
}

async function associateAnonymousAssessment(user: User) {
  const draft = readAssessmentDraft();
  if (!isCompleteAssessmentDraft(draft) || draft.saveToAccount !== true) {
    return false;
  }
  const saved = await saveAssessmentResult(draft);

  if (draft) {
    window.sessionStorage.removeItem('orivon_assessment_draft_v1');
    window.sessionStorage.removeItem('orivon_active_assessment_id_v1');
    window.localStorage.removeItem('orivon_assessment_handoff_v1');
  }
  return saved.changed;
}

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  assessmentSaved: boolean;
  assessmentSyncError: string;
  configured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, locale: string) => Promise<'confirmed' | 'pending'>;
  resetPassword: (email: string, locale: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const configured = hasSupabaseConfig();
  const [session, setSession] = useState<Session | null>(null);
  const userId = session?.user?.id ?? null;
  const [loading, setLoading] = useState(true);
  const [linkingAssessment, setLinkingAssessment] = useState(false);
  const [assessmentSaved, setAssessmentSaved] = useState(false);
  const [assessmentSyncError, setAssessmentSyncError] = useState('');
  const linkedAssessmentKey = useRef<string | null>(null);
  const linkingAssessmentKey = useRef<string | null>(null);

  useEffect(() => {
    if (!configured) {
      setLoading(false);
      return;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [configured]);

  useEffect(() => {
    if (!session?.user) {
      linkedAssessmentKey.current = null;
      linkingAssessmentKey.current = null;
      setLinkingAssessment(false);
      setAssessmentSaved(false);
      setAssessmentSyncError('');
      return;
    }
    let active = true;
    window.posthog?.identify?.(session.user.id, { authenticated: true });
    track('app_opened', { authenticated: true });

    const syncAssessment = () => {
      const draft = readAssessmentDraft();
      const key = assessmentSyncKey(session.user.id, draft);
      if (
        linkedAssessmentKey.current === key ||
        linkingAssessmentKey.current === key
      ) {
        return;
      }
      // Route changes and tab focus must never blank the authenticated app.
      // Only a complete assessment handoff is worth entering the linking state.
      if (!isCompleteAssessmentDraft(draft) || draft.saveToAccount !== true) {
        linkedAssessmentKey.current = key;
        return;
      }
      linkingAssessmentKey.current = key;
      if (active) {
        setLinkingAssessment(true);
        setAssessmentSaved(false);
        setAssessmentSyncError('');
      }
      associateAnonymousAssessment(session.user)
        .then((linked) => {
          linkedAssessmentKey.current = assessmentSyncKey(
            session.user.id,
            readAssessmentDraft()
          );
          if (linked) {
            if (active) setAssessmentSaved(true);
            track('assessment_linked_to_account', {
              authenticated: true,
              source: 'assessment_retake_or_signup'
            });
          }
        })
        .catch((error) => {
          console.error('Assessment association failed', error);
          if (active) {
            setAssessmentSyncError(
              error instanceof Error
                ? error.message
                : 'Unable to save the completed assessment'
            );
          }
        })
        .finally(() => {
          linkingAssessmentKey.current = null;
          if (active) setLinkingAssessment(false);
        });
    };

    syncAssessment();
    window.addEventListener('pageshow', syncAssessment);
    window.addEventListener('storage', syncAssessment);
    return () => {
      active = false;
      window.removeEventListener('pageshow', syncAssessment);
      window.removeEventListener('storage', syncAssessment);
    };
  }, [userId]);

  // Kept referentially stable across TOKEN_REFRESHED events for the same user
  // (Supabase refreshes the token whenever the tab regains focus, which would
  // otherwise re-trigger every effect that depends on `user`).
  const stableUser = useMemo(() => session?.user ?? null, [userId]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: stableUser,
      loading: loading || linkingAssessment,
      assessmentSaved,
      assessmentSyncError,
      configured,
      async signIn(email, password) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },
      async signUp(email, password, locale) {
        const sourceParams = new URLSearchParams(window.location.search);
        const intent = sourceParams.get('intent');
        const billingPeriod =
          sourceParams.get('billing') === 'annual' ? 'annual' : 'monthly';
        const offer = sourceParams.get('offer') === 'career_sprint_30d'
          ? 'career_sprint_30d'
          : '';
        const redirectParams = new URLSearchParams({ lang: locale });
        if (intent === 'professional' || intent === 'activate') {
          redirectParams.set('intent', intent);
        }
        if (intent === 'professional') {
          redirectParams.set('billing', billingPeriod);
          if (offer) redirectParams.set('offer', offer);
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { locale },
            emailRedirectTo: `${window.location.origin}/app/?${redirectParams.toString()}`
          }
        });
        if (error) throw error;
        return data.session ? 'confirmed' : 'pending';
      },
      async resetPassword(email, locale) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/app/reset-password?lang=${locale}`
        });
        if (error) throw error;
      },
      async updatePassword(password) {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
      },
      async signOut() {
        await supabase.auth.signOut();
        window.posthog?.reset?.();
      }
    }),
    [
      assessmentSaved,
      assessmentSyncError,
      configured,
      linkingAssessment,
      loading,
      session,
      stableUser
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
